import { Controller } from "@hotwired/stimulus"

// CONEK-8782 — show the inspection disclaimer before any NEW inspection starts.
//
// Connects to data-controller="inspections--disclaimer" on the wrapper that the
// `inspections/shared/disclaimer_modal` partial renders around its
// Ui::AlertModalComponent (a `ui--modal` <dialog>).
//
// A capture-phase click listener on `document` holds back every primary click
// on a control that starts an inspection:
//   * any element inside `[data-inspection-disclaimer]`, or
//   * a link (`a[href]`) or submit button inside `[data-inspection-disclaimer-scope]`
//     (for components that accept no `data:` on their own controls).
// Capture on `document` runs before Turbo's link and form handling (window
// bubble / submit) and before any Stimulus action on the control itself (for
// example `ui--modal-opener`), so stopping the event there holds all of them.
// Only clicks are held: a programmatic `form.requestSubmit()` / `form.submit()`
// or `Turbo.visit()` dispatches no click and is NOT intercepted.
//
// OK marks the request as confirmed and closes the dialog. The continuation
// runs on the dialog's NATIVE `close` event, which fires on every way out (OK,
// X, backdrop and Esc — Esc closes natively and never dispatches `modal:close`).
// When confirmed, the original control is approved and clicked again, so the
// Turbo visit, the `button_to` submit or the chained opener runs unchanged.
// Anything else cancels: nothing proceeds.
//
// The leg page and the mobile device panel are re-rendered by Turbo Streams,
// so the control clicked may be gone by the time OK is pressed. Its identity
// is snapshotted at intercept time and, if it was replaced, the live twin
// with the same identity is clicked instead; a link with no twin left is
// followed with a Turbo visit. Anything else does nothing.
const MARKER_SELECTOR = "[data-inspection-disclaimer]"
const SCOPE_SELECTOR = "[data-inspection-disclaimer-scope]"
const SCOPED_CONTROL_SELECTOR =
  'a[href], button, input[type="submit"], input[type="image"]'
// Every element that can be a marked control, for re-finding a replaced one.
const CANDIDATE_SELECTOR = `${MARKER_SELECTOR}, ${SCOPE_SELECTOR} :is(${SCOPED_CONTROL_SELECTOR})`
// Never part of a form's identity: it rotates with every render.
const IGNORED_FORM_FIELDS = new Set(["authenticity_token"])

// Approvals for the replayed click. Module-level, so every controller
// instance (two layouts mid-swap, a stale one not yet disconnected) lets the
// replay through, and a WeakSet, so a removed control is never kept alive.
// An approval lives only while `control.click()` dispatches synchronously: it
// is cleared as soon as that returns, even if no event fired (a disabled
// button), so it can never let a later, real click through.
const approved = new WeakSet()

export default class extends Controller {
  connect() {
    this.pending = null
    this.pendingIdentity = null
    this.confirmed = false

    this.dialog = this.element.querySelector("dialog")

    this.handleClick = this.handleClick.bind(this)
    this.handleDialogClose = this.handleDialogClose.bind(this)
    this.handleBeforeCache = this.handleBeforeCache.bind(this)

    document.addEventListener("click", this.handleClick, true)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)
    if (this.dialog) {
      this.dialog.addEventListener("close", this.handleDialogClose)
    }
  }

  disconnect() {
    document.removeEventListener("click", this.handleClick, true)
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    if (this.dialog) {
      this.dialog.removeEventListener("close", this.handleDialogClose)
    }
    this.reset()
  }

  // OK in the modal footer.
  confirm() {
    this.confirmed = this.pending !== null
    this.closeModal()
  }

  handleClick(event) {
    // A wrapper removed without Stimulus noticing (its observer stopped, or a
    // body swap outside Turbo) must never keep intercepting: drop the listener
    // so the disclaimer of the CURRENT page is the one that answers.
    // This fails OPEN (the click goes through), and that is safe only because
    // it is unreachable in practice: the partial is rendered by the layouts in
    // the <body>, outside every Turbo Frame and Stream target, so a page swap
    // brings its own connected wrapper. Keep it there: moved inside a frame or
    // a stream target, it could be detached while the page still has controls.
    if (!this.element.isConnected) {
      this.disconnect()
      return
    }

    if (event.button !== 0) return

    const control = this.matchControl(event.target)
    if (!control) return

    // The replay of an approved click: let it through. Not deleted here, so a
    // second controller instance listening on `document` lets it through too;
    // `proceed` clears it once the click returns.
    if (approved.has(control)) return

    // Without the dialog there is nothing to acknowledge; fail closed rather
    // than let an inspection start without the disclaimer.
    event.preventDefault()
    event.stopImmediatePropagation()

    // Already showing (or closing): ignore the extra click.
    if (!this.dialog || this.dialog.open || this.pending) return

    this.pending = control
    this.pendingIdentity = identityOf(control)
    this.confirmed = false
    this.openModal()
  }

  handleDialogClose() {
    const control = this.pending
    const identity = this.pendingIdentity
    const proceed = this.confirmed && control !== null
    this.reset()
    if (!proceed) return

    // Deferred to the next task so every other `close` listener (focus
    // return, a page's own bookkeeping) has run before the original action —
    // e.g. a chained confirmation dialog — starts.
    setTimeout(() => this.proceed(control, identity), 0)
  }

  handleBeforeCache() {
    this.reset()
    if (this.dialog?.open) this.dialog.close()
  }

  // --- helpers ---

  // Replays the approved click on the control, or on its live twin when a
  // Turbo Stream replaced it while the disclaimer was up.
  proceed(control, identity) {
    const target = control.isConnected ? control : this.findLiveTwin(identity)

    if (target) {
      approved.add(target)
      try {
        target.click()
      } finally {
        approved.delete(target)
      }
      return
    }

    if (identity.tag === "A" && identity.href) visit(identity.href)
  }

  findLiveTwin(identity) {
    // Without a testid, an href or a form action there is nothing that tells
    // one marked control from another: never guess.
    if (!identity.testId && !identity.href && !identity.action) return null

    const twins = Array.from(
      document.querySelectorAll(CANDIDATE_SELECTOR),
    ).filter(
      (element) =>
        this.matchControl(element) === element &&
        sameIdentity(identityOf(element), identity),
    )
    // The same control may be rendered twice for two breakpoints; prefer the
    // one the reader can see.
    return twins.find(isRendered) ?? twins[0] ?? null
  }

  matchControl(target) {
    if (!(target instanceof Element)) return null

    const marked = target.closest(MARKER_SELECTOR)
    if (marked) return marked

    const control = target.closest(SCOPED_CONTROL_SELECTOR)
    if (!control || !control.closest(SCOPE_SELECTOR)) return null
    if (control instanceof HTMLAnchorElement) return control
    if (control instanceof HTMLButtonElement) {
      return control.type === "submit" ? control : null
    }
    return control
  }

  reset() {
    this.pending = null
    this.pendingIdentity = null
    this.confirmed = false
  }

  modalController() {
    if (!this.dialog) return null
    return this.application.getControllerForElementAndIdentifier(
      this.dialog,
      "ui--modal",
    )
  }

  openModal() {
    const controller = this.modalController()
    if (controller) {
      controller.open()
    } else if (this.dialog && !this.dialog.open) {
      this.dialog.showModal()
    }
  }

  closeModal() {
    const controller = this.modalController()
    if (controller) {
      controller.close()
    } else if (this.dialog?.open) {
      this.dialog.close()
    }
  }
}

// What a re-rendered copy of the same control shares with the original: its
// test id, its tag, and where it goes (the link's href, or the form's action
// plus its fields — `button_to` keeps `asset_id` in a hidden input).
function identityOf(control) {
  const form = control.form ?? null
  return {
    tag: control.tagName,
    testId:
      control.getAttribute("data-testid") ??
      control.getAttribute("data-test-id"),
    href: control.getAttribute("href"),
    action: form ? form.getAttribute("action") : null,
    fields: form ? formFields(form) : null,
  }
}

function formFields(form) {
  const fields = []
  for (const [name, value] of new FormData(form)) {
    if (!IGNORED_FORM_FIELDS.has(name)) fields.push(`${name}=${String(value)}`)
  }
  return fields.sort().join("&")
}

function sameIdentity(a, b) {
  return (
    a.tag === b.tag &&
    a.testId === b.testId &&
    a.href === b.href &&
    a.action === b.action &&
    a.fields === b.fields
  )
}

function isRendered(element) {
  return element.getClientRects().length > 0
}

function visit(href) {
  if (window.Turbo?.visit) {
    window.Turbo.visit(href)
  } else {
    window.location.assign(href)
  }
}
